import { Button } from "@pepperstone/pepper-ui-kit";
import clsx from "clsx";
import React, {
  type PropsWithChildren,
  type ReactNode,
  useEffect,
  useRef,
} from "react";
import style from "./BaseModal.module.css";

const SCROLL_DELTA_TO_HIDE = 50;

export type BaseModalProps = {
  testId: string;
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  isOpen: boolean;
  onClose: () => void;
  maxWidth?: number;
  maxHeight?: number;
  height?: string;
  hideCloseIconOnMobile?: boolean;
  headerClassName?: string;
  dialogClassName?: string;
  isCancelable?: boolean;
  onSubmit?: () => void;
  onCancel?: () => void;
  submitButtonContent?: ReactNode;
  isSubmitButtonDisabled?: boolean;
  isSubmitButtonLoading?: boolean;
  cancelButtonContent?: ReactNode;
};

export const BaseModal = ({
  testId,
  title,
  description,
  children,
  isOpen,
  onClose,
  maxHeight,
  maxWidth,
  height,
  hideCloseIconOnMobile = true,
  headerClassName,
  dialogClassName,
  isCancelable,
  onSubmit,
  onCancel,
  submitButtonContent,
  isSubmitButtonDisabled = false,
  isSubmitButtonLoading = false,
  cancelButtonContent,
  ...props
}: PropsWithChildren<
  Omit<React.DialogHTMLAttributes<HTMLDialogElement>, "open" | "title"> &
    BaseModalProps
>) => {
  const startY = useRef<number | null>(null);
  const dialogRef = useRef<HTMLDialogElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      //this is important otherwise focus is not trapped into the modal, if you just use a prop for the open attribute it won't work.
      //I suggest to create a useState in your parent/smart component
      dialogRef?.current?.showModal();
    } else {
      dialogRef?.current?.close();
    }
  }, [isOpen]);

  const focusDialog = () => {
    dialogRef.current?.focus();
  };

  const handleStart = (e: React.TouchEvent | React.MouseEvent) => {
    focusDialog();
    startY.current = "touches" in e ? e.touches[0].clientY : e.clientY;
  };

  const handleEnd = (e: React.TouchEvent | React.MouseEvent) => {
    focusDialog();
    if (startY.current === null) return;
    const endY =
      "changedTouches" in e ? e.changedTouches[0].clientY : e.clientY;
    const delta = endY - startY.current;
    if (delta > SCROLL_DELTA_TO_HIDE) {
      onClose();
    }
    startY.current = null;
  };

  return (
    <>
      <dialog
        style={{ height, maxHeight, maxWidth }}
        data-testid={testId}
        className={clsx(
          style["base-modal"],
          isOpen ? style["base-modal-open"] : style["base-modal-closed"],
          style.DialogFlex,
          "ps-rounded-md",
          "ps-p-lg",
          "ps-border-transparent",
          "ps-flex-col",
          dialogClassName
        )}
        ref={dialogRef}
        onTouchStart={focusDialog}
        onTouchMove={focusDialog}
        onCancel={(e) => {
          const target = e.target;
          // catch and check if the onCancel event came from the the element itself or it was bubbled from another onCancel event.
          if (target === e.currentTarget && onClose && isCancelable) {
            onClose(e);
          }
        }}
        onKeyDown={(e) => {
          //catch for catching specific cases where dialog should not be closable
          if (e.key === "Escape" && !isCancelable) {
            e.preventDefault();
          }
        }}
        {...props}
      >
        <section className={style["base-modal-wrapper"]}>
          {/* Drag handle */}
          <div
            onMouseDown={handleStart}
            onMouseUp={handleEnd}
            onTouchStart={handleStart}
            onTouchEnd={handleEnd}
          >
            <div
              className={clsx(
                hideCloseIconOnMobile && style["base-modal-drag-handler"]
              )}
            />
            <header
              className={clsx(
                style["base-modal-header"],
                hideCloseIconOnMobile &&
                  style["base-modal-header-hidden-close-btn"],
                headerClassName
              )}
            >
              <div className={style["base-modal-header-content"]}>
                {title && (
                  <h3 className={style["base-modal-title"]}>{title}</h3>
                )}
                <Button
                  aria-label="close-button"
                  variant="ps-btn-transparent"
                  type="button"
                  size="ps-btn-xs"
                  onClick={onClose}
                  icon="cross"
                  style={{ marginInlineStart: "auto" }}
                />
              </div>
              {description && (
                <p className={style["base-modal-description"]}>{description}</p>
              )}
            </header>
          </div>
          {children}
          {(submitButtonContent || cancelButtonContent) && (
            <div className="ps-flex ps-gap-2 ps-mt-4">
              <div style={{ marginInlineStart: "auto" }} />
              {cancelButtonContent && (
                <Button
                  variant="ps-btn-transparent"
                  type="button"
                  onClick={() => {
                    if (onCancel) onCancel();
                  }}
                  className="ps-ml-full"
                >
                  {cancelButtonContent}
                </Button>
              )}
              {submitButtonContent && (
                <Button
                  variant="ps-btn-primary"
                  type="button"
                  disabled={isSubmitButtonDisabled || isSubmitButtonLoading}
                  isLoading={isSubmitButtonLoading}
                  onClick={() => {
                    if (onSubmit) onSubmit();
                  }}
                  className="ps-ml-full"
                >
                  {submitButtonContent}
                </Button>
              )}
            </div>
          )}
        </section>
      </dialog>
      {isOpen && <div className={style.overlay} onClick={onClose} />}
    </>
  );
};
